import {
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VoiceDictationService } from 'mojo-voice-dictation';
import { Subscription } from 'rxjs';

export interface VoiceDictationState {
  isListening: boolean;
  isSpeaking: boolean;
  currentLevel: number;
}

@Component({
  selector: 'fastr-ui-voice-dictation',
  templateUrl: './voice-dictation.component.html',
  styleUrls: ['./voice-dictation.component.scss'],
  providers: [VoiceDictationService],
})
export class VoiceDictationComponent implements OnDestroy {
  @Input() size: 'default' | 'compact' = 'default';
  @Input()
  set disabled(value: boolean) {
    this._disabled = value;

    if (value && this.dictation.isListening) {
      this.stop();
    }
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Output() transcript = new EventEmitter<string>();
  @Output() stateChange = new EventEmitter<VoiceDictationState>();

  private readonly dictation = inject(VoiceDictationService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly subs = new Subscription();
  private listeningStateInterval: ReturnType<typeof setInterval> | null = null;
  private lastListeningState = false;
  private _disabled = false;

  constructor() {
    this.subs.add(this.dictation.transcript$.subscribe((text) => this.transcript.emit(text)));
    this.subs.add(
      this.dictation.error$.subscribe((msg) => {
        this.snackBar.open(msg, undefined, {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      }),
    );
  }

  @HostBinding('class.is-compact')
  get isCompact(): boolean {
    return this.size === 'compact';
  }

  @HostBinding('class.is-disabled')
  get isDisabled(): boolean {
    return this.disabled;
  }

  get isSpeechSupported(): boolean {
    return this.dictation.isSpeechSupported;
  }

  get isListening(): boolean {
    return this.dictation.isListening;
  }

  get isSpeaking(): boolean {
    return this.dictation.isSpeaking;
  }

  get currentLevel(): number {
    return this.dictation.currentLevel;
  }

  get microphoneAccessDenied(): boolean {
    return this.dictation.microphoneAccessDenied;
  }

  ngOnDestroy(): void {
    this.stopListeningStateMonitor();
    this.dictation.stop();
    this.subs.unsubscribe();
  }

  async start(): Promise<void> {
    if (this.disabled || !this.dictation.isSpeechSupported) {
      return;
    }

    await this.dictation.start();
    this.syncListeningState();
    this.emitState();
    this.startListeningStateMonitor();
  }

  stop(): void {
    this.dictation.stop();
    this.syncListeningState();
    this.emitState();
    this.stopListeningStateMonitor();
  }

  private startListeningStateMonitor(): void {
    this.stopListeningStateMonitor();

    if (!this.dictation.isListening) {
      return;
    }

    this.listeningStateInterval = setInterval(() => {
      this.syncListeningState();
      this.emitState();

      if (!this.dictation.isListening) {
        this.stopListeningStateMonitor();
      }
    }, 100);
  }

  private stopListeningStateMonitor(): void {
    if (this.listeningStateInterval) {
      clearInterval(this.listeningStateInterval);
      this.listeningStateInterval = null;
    }
  }

  private syncListeningState(): void {
    if (this.lastListeningState === this.dictation.isListening) {
      return;
    }

    this.lastListeningState = this.dictation.isListening;
  }

  private emitState(): void {
    this.stateChange.emit({
      isListening: this.dictation.isListening,
      isSpeaking: this.dictation.isSpeaking,
      currentLevel: this.dictation.currentLevel,
    });
  }
}
